/**
 * Product Line Manager Web Component
 * Handles dynamic product lines, calculations, and metrics toggle
 */

if (!customElements.get('product-line-manager')) {
  class ProductLineManager extends HTMLElement {
    constructor() {
      super();
      this.lineCount = 0;
      this.maxLines = 10;
      this.currency = this.dataset.currency || '€';
      // The shop's own money format, e.g. "€{{amount_with_comma_separator}}".
      // Without it the quote would print €232.20 where the cart prints €232,20 —
      // the same dot means "thousands" in one place and "decimals" in the other.
      this.moneyFormat = this.dataset.moneyFormat || '';

      // Parse customer B2B discount rates (per m²)
      this.customerDiscounts = {};
      try {
        const discountsJson = this.dataset.customerDiscounts;
        if (discountsJson) {
          const parsed = JSON.parse(discountsJson);
          // Normalize keys to handle format
          for (const [key, rate] of Object.entries(parsed)) {
            const normalized = key.toLowerCase().trim()
              .replace(/\s+/g, '-').replace(/\//g, '-')
              .replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-')
              .replace(/^-|-$/g, '');
            this.customerDiscounts[normalized] = parseFloat(rate) || 0;
          }
        }
      } catch (e) {
        console.error('Failed to parse customer discounts:', e);
      }

      // Raw-material surcharge factor (shop metafield, 1.0 = none, 1.08 = +8%).
      // Applied after the discount, mirroring product-table-row.liquid and the
      // b2b-price-transform cart function, so the quote matches the cart.
      this.surchargeFactor = parseFloat(this.dataset.surchargeFactor);
      if (!(this.surchargeFactor > 0)) {
        this.surchargeFactor = 1;
      }

      // Conversion constants
      this.M_TO_FT = 3.28084;
      this.M2_TO_FT2 = 10.7639;
    }

    /**
     * Format an amount the way the rest of the storefront does, by following
     * the shop's money_format. Handles Shopify's four placeholders; anything
     * around them (currency symbol, suffix) is kept as-is. Falls back to the
     * old symbol + dot notation when no format was passed.
     * @param {number} amount in major units, not cents
     */
    formatMoney(amount) {
      if (!this.moneyFormat) {
        return this.currency + ' ' + amount.toFixed(2);
      }

      const placeholder = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!placeholder) return this.moneyFormat;

      const name = placeholder[1];
      const commaSeparator = name.indexOf('comma_separator') !== -1;
      const decimals = name.indexOf('no_decimals') !== -1 ? 0 : 2;
      const thousandsMark = commaSeparator ? '.' : ',';
      const decimalMark = commaSeparator ? ',' : '.';

      const parts = Math.abs(amount).toFixed(decimals).split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsMark);
      const fraction = parts[1] ? decimalMark + parts[1] : '';
      const value = (amount < 0 ? '-' : '') + whole + fraction;

      return this.moneyFormat.replace(placeholder[0], value);
    }

    connectedCallback() {
      this.template = this.querySelector('#product-line-template');
      this.container = this.querySelector('[data-product-lines]');
      this.addButton = this.querySelector('[data-add-line]');

      if (!this.template || !this.container || !this.addButton) {
        console.error('ProductLineManager: Required elements not found');
        return;
      }

      // Add initial product line
      this.addProductLine();

      // Setup add button
      this.addButton.addEventListener('click', () => this.addProductLine());
    }

    addProductLine() {
      if (this.lineCount >= this.maxLines) {
        alert(`Maximum ${this.maxLines} product lines allowed.`);
        return;
      }

      this.lineCount++;
      const lineNumber = this.lineCount;

      // Clone template
      const clone = this.template.content.cloneNode(true);
      const line = clone.querySelector('[data-product-line]');

      // Set line number
      const lineNumberEl = line.querySelector('[data-line-number]');
      if (lineNumberEl) {
        lineNumberEl.textContent = lineNumber;
      }

      // Update field names with line number
      this.updateFieldNames(line, lineNumber);

      // Setup event listeners for this line
      this.setupLineEventListeners(line, lineNumber);

      // Show/hide remove button (hide for first line)
      const removeButton = line.querySelector('[data-remove-line]');
      if (removeButton && lineNumber > 1) {
        removeButton.style.display = 'flex';
      }

      // Append to container
      this.container.appendChild(line);

      // Update add button visibility
      this.updateAddButtonVisibility();
    }

    updateFieldNames(line, lineNumber) {
      // Update hidden field names for form submission
      const hiddenFields = {
        '[data-hidden-product-type]': `contact[product_line_${lineNumber}_type]`,
        '[data-hidden-roll-width]': `contact[product_line_${lineNumber}_width]`,
        '[data-hidden-roll-length]': `contact[product_line_${lineNumber}_length]`,
        '[data-hidden-number-rolls]': `contact[product_line_${lineNumber}_rolls]`,
        '[data-hidden-total-area]': `contact[product_line_${lineNumber}_area]`,
        '[data-hidden-total-price]': `contact[product_line_${lineNumber}_price]`,
        '[data-hidden-comments]': `contact[product_line_${lineNumber}_comments]`
      };

      for (const [selector, name] of Object.entries(hiddenFields)) {
        const field = line.querySelector(selector);
        if (field) {
          field.name = name;
        }
      }
    }

    setupLineEventListeners(line, lineNumber) {
      // Product type dropdown
      const productType = line.querySelector('[data-product-type]');
      // Roll width dropdown
      const rollWidth = line.querySelector('[data-roll-width]');
      // Roll length input
      const rollLength = line.querySelector('[data-roll-length]');
      // Number of rolls input
      const numberRolls = line.querySelector('[data-number-rolls]');
      // Comments textarea
      const comments = line.querySelector('[data-line-comments]');
      // Metrics toggle
      const metricsToggle = line.querySelector('[data-metrics-toggle]');
      // Remove button
      const removeButton = line.querySelector('[data-remove-line]');

      // Track metrics state for this line
      line.isImperial = false;

      // Calculate on any input change
      const calculateHandler = () => this.calculateLine(line);

      if (productType) {
        productType.addEventListener('change', calculateHandler);
      }
      if (rollWidth) {
        rollWidth.addEventListener('change', calculateHandler);
      }
      if (rollLength) {
        rollLength.addEventListener('input', calculateHandler);
      }
      if (numberRolls) {
        numberRolls.addEventListener('input', calculateHandler);
      }

      // Sync comments to hidden field
      if (comments) {
        comments.addEventListener('input', () => {
          const hiddenComments = line.querySelector('[data-hidden-comments]');
          if (hiddenComments) {
            hiddenComments.value = comments.value;
          }
        });
      }

      // Metrics toggle
      if (metricsToggle) {
        metricsToggle.addEventListener('click', () => {
          line.isImperial = !line.isImperial;
          metricsToggle.classList.toggle('active', line.isImperial);
          this.updateLabels(line, line.isImperial);
          this.calculateLine(line);
        });
      }

      // Remove button
      if (removeButton) {
        removeButton.addEventListener('click', () => this.removeProductLine(line));
      }
    }

    updateLabels(line, isImperial) {
      const widthLabel = line.querySelector('[data-width-label]');
      const lengthLabel = line.querySelector('[data-length-label]');
      const areaLabel = line.querySelector('[data-area-label]');

      if (widthLabel) {
        widthLabel.textContent = isImperial ? 'Roll width (ft)' : 'Roll width (m)';
      }
      if (lengthLabel) {
        lengthLabel.textContent = isImperial ? 'Roll length (ft)' : 'Roll length (m)';
      }
      if (areaLabel) {
        areaLabel.textContent = isImperial ? 'Total ft²' : 'Total m²';
      }

      // Update roll width options
      const rollWidth = line.querySelector('[data-roll-width]');
      if (rollWidth) {
        const options = rollWidth.querySelectorAll('option[value]');
        options.forEach(option => {
          const metricValue = parseFloat(option.value);
          if (metricValue > 0) {
            const displayValue = isImperial
              ? (metricValue * this.M_TO_FT).toFixed(2)
              : metricValue.toFixed(2);
            option.textContent = displayValue;
          }
        });
      }

      // Convert roll length max (50 m hard limit) to the active unit
      const rollLengthInput = line.querySelector('[data-roll-length]');
      if (rollLengthInput) {
        rollLengthInput.max = isImperial
          ? (50 * this.M_TO_FT).toFixed(2)
          : '50';
      }
    }

    calculateLine(line) {
      const productType = line.querySelector('[data-product-type]');
      const rollWidth = line.querySelector('[data-roll-width]');
      const rollLength = line.querySelector('[data-roll-length]');
      const numberRolls = line.querySelector('[data-number-rolls]');
      const totalArea = line.querySelector('[data-total-area]');
      const totalPrice = line.querySelector('[data-total-price]');

      // Hidden fields for submission (always in metric)
      const hiddenProductType = line.querySelector('[data-hidden-product-type]');
      const hiddenRollWidth = line.querySelector('[data-hidden-roll-width]');
      const hiddenRollLength = line.querySelector('[data-hidden-roll-length]');
      const hiddenNumberRolls = line.querySelector('[data-hidden-number-rolls]');
      const hiddenTotalArea = line.querySelector('[data-hidden-total-area]');
      const hiddenTotalPrice = line.querySelector('[data-hidden-total-price]');

      // Get values
      const selectedProduct = productType?.selectedOptions[0];
      let pricePerM2 = selectedProduct ? parseFloat(selectedProduct.dataset.price) || 0 : 0;

      // Apply customer B2B discount if available
      if (pricePerM2 > 0 && selectedProduct?.value) {
        const normalizedType = selectedProduct.value.toLowerCase().trim()
          .replace(/\s+/g, '-').replace(/\//g, '-')
          .replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
        const discount = this.customerDiscounts[normalizedType] || 0;
        if (discount > 0) {
          pricePerM2 = Math.max(0, pricePerM2 - discount);
        }
      }

      // Surcharge comes after the discount, never before
      pricePerM2 = pricePerM2 * this.surchargeFactor;

      const widthM = parseFloat(rollWidth?.value) || 0; // Always stored in meters
      const lengthInput = parseFloat(rollLength?.value) || 0;
      const rolls = parseInt(numberRolls?.value) || 0;

      // Convert length input to meters if imperial
      const lengthM = line.isImperial ? lengthInput / this.M_TO_FT : lengthInput;

      // Calculate area in m²
      const areaM2 = widthM * lengthM * rolls;

      // Calculate price
      const price = areaM2 * pricePerM2;

      // Display values (converted if imperial)
      if (totalArea) {
        if (areaM2 > 0) {
          const displayArea = line.isImperial
            ? (areaM2 * this.M2_TO_FT2).toFixed(2)
            : areaM2.toFixed(2);
          const unit = line.isImperial ? ' ft²' : ' m²';
          totalArea.value = displayArea + unit;
        } else {
          totalArea.value = '';
        }
      }

      if (totalPrice) {
        if (price > 0) {
          totalPrice.value = this.formatMoney(price);
        } else {
          totalPrice.value = '';
        }
      }

      // Update hidden fields (always in metric for submission)
      if (hiddenProductType && selectedProduct) {
        hiddenProductType.value = selectedProduct.value;
      }
      if (hiddenRollWidth) {
        hiddenRollWidth.value = widthM > 0 ? widthM + 'm' : '';
      }
      if (hiddenRollLength) {
        hiddenRollLength.value = lengthM > 0 ? lengthM.toFixed(2) + 'm' : '';
      }
      if (hiddenNumberRolls) {
        hiddenNumberRolls.value = rolls > 0 ? rolls : '';
      }
      if (hiddenTotalArea) {
        hiddenTotalArea.value = areaM2 > 0 ? areaM2.toFixed(2) + ' m²' : '';
      }
      if (hiddenTotalPrice) {
        hiddenTotalPrice.value = price > 0 ? this.formatMoney(price) : '';
      }
    }

    removeProductLine(line) {
      if (this.lineCount <= 1) {
        return; // Don't remove the last line
      }

      line.remove();
      this.lineCount--;

      // Renumber remaining lines
      this.renumberLines();

      // Update add button visibility
      this.updateAddButtonVisibility();
    }

    renumberLines() {
      const lines = this.container.querySelectorAll('[data-product-line]');
      lines.forEach((line, index) => {
        const lineNumber = index + 1;

        // Update displayed line number
        const lineNumberEl = line.querySelector('[data-line-number]');
        if (lineNumberEl) {
          lineNumberEl.textContent = lineNumber;
        }

        // Update hidden field names
        this.updateFieldNames(line, lineNumber);

        // Show/hide remove button
        const removeButton = line.querySelector('[data-remove-line]');
        if (removeButton) {
          removeButton.style.display = lineNumber > 1 ? 'flex' : 'none';
        }
      });
    }

    updateAddButtonVisibility() {
      if (this.addButton) {
        this.addButton.style.display = this.lineCount >= this.maxLines ? 'none' : 'inline-flex';
      }
    }
  }

  customElements.define('product-line-manager', ProductLineManager);
}

/**
 * Request Batch Form Submission Handler
 * Sends data to Cloudflare Worker which saves to Shopify Metaobjects
 */
(function() {
  const WORKER_URL = 'https://one-dna-tool.niice-dev3.niicelab.nl/api/batch-request';

  document.addEventListener('DOMContentLoaded', function() {
    const form = document.getElementById('RequestBatchForm');
    if (!form) return;

    form.addEventListener('submit', async function(e) {
      e.preventDefault();
      e.stopPropagation();

      const submitButton = form.querySelector('button[type="submit"]');
      const originalText = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting...';

      try {
        // Collect form data for Worker (metaobject storage)
        const formData = collectFormData(form);

        // Send to app API to save in metaobjects
        const response = await fetch(WORKER_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Api-Key': 'odn-batch-2026-s3cur3k3y',
          },
          body: JSON.stringify(formData),
        });

        const result = await response.json();

        if (result.success) {
          // Show success message
          showFormStatus(form, 'success', 'Thank you! Your production order request has been submitted. We will contact you within 2 business days.');
          form.reset();

          // Re-initialize product lines
          const manager = document.querySelector('product-line-manager');
          if (manager) {
            manager.container.innerHTML = '';
            manager.lineCount = 0;
            manager.addProductLine();
          }
        } else {
          showFormStatus(form, 'error', 'There was an error submitting your request. Please try again or contact us directly.');
          console.error('Form submission error:', result);
        }
      } catch (error) {
        showFormStatus(form, 'error', 'There was an error submitting your request. Please try again or contact us directly.');
        console.error('Form submission error:', error);
      } finally {
        submitButton.disabled = false;
        submitButton.textContent = originalText;
      }
    });
  });

  function collectFormData(form) {
    // Basic customer info
    const data = {
      company_name: form.querySelector('[name="contact[company_name]"]')?.value || '',
      contact_name: [
        form.querySelector('[name="contact[first_name]"]')?.value || '',
        form.querySelector('[name="contact[last_name]"]')?.value || ''
      ].filter(Boolean).join(' '),
      email: form.querySelector('[name="contact[email]"]')?.value || '',
      phone: form.querySelector('[name="contact[phone]"]')?.value || '',
      delivery_address: form.querySelector('[name="contact[delivery_address]"]')?.value || '',
      postal_code: form.querySelector('[name="contact[postal_code]"]')?.value || '',
      city: form.querySelector('[name="contact[city]"]')?.value || '',
      country: form.querySelector('[name="contact[country]"]')?.value || '',
      project_name: form.querySelector('[name="contact[project_name]"]')?.value || '',
      po_number: form.querySelector('[name="contact[po_number]"]')?.value || '',
      application_area: form.querySelector('[name="contact[application_area]"]')?.value || '',
      delivery_date: form.querySelector('[name="contact[delivery_date]"]')?.value || '',
      project_description: form.querySelector('[name="contact[project_description]"]')?.value || '',
      incoterms: form.querySelector('[name="contact[incoterms]"]')?.value || '',
      delivery_instructions: form.querySelector('[name="contact[delivery_instructions]"]')?.value || '',
    };

    // Collect product lines
    const productLines = [];
    const lines = document.querySelectorAll('[data-product-line]');
    lines.forEach((line, index) => {
      const lineNum = index + 1;
      const productType = line.querySelector('[data-hidden-product-type]')?.value;
      if (productType) {
        productLines.push({
          line: lineNum,
          product_type: productType,
          roll_width: line.querySelector('[data-hidden-roll-width]')?.value || '',
          roll_length: line.querySelector('[data-hidden-roll-length]')?.value || '',
          number_of_rolls: line.querySelector('[data-hidden-number-rolls]')?.value || '',
          total_area: line.querySelector('[data-hidden-total-area]')?.value || '',
          total_price: line.querySelector('[data-hidden-total-price]')?.value || '',
          comments: line.querySelector('[data-hidden-comments]')?.value || '',
        });
      }
    });

    // Format product lines as readable text
    data.product_lines = productLines.map(pl =>
      `Line ${pl.line}: ${pl.product_type} | ${pl.roll_width} x ${pl.roll_length} | ${pl.number_of_rolls} rolls | ${pl.total_area} | ${pl.total_price}${pl.comments ? ' | Note: ' + pl.comments : ''}`
    ).join('\n');

    return data;
  }

  function showFormStatus(form, type, message) {
    // Remove existing status
    const existingStatus = form.querySelector('.form-status');
    if (existingStatus) {
      existingStatus.remove();
    }

    // Create status element
    const status = document.createElement('div');
    status.className = `form-status form-status--${type}`;
    status.tabIndex = -1;

    const icon = type === 'success'
      ? '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>';

    status.innerHTML = icon + ' ' + message;

    // Insert at top of form
    form.insertBefore(status, form.firstChild);
    status.focus();

    // Scroll to status
    status.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
})();
